import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

/**
 * Applies admin-configured theme settings (primary color, border radius, font)
 * as CSS custom properties on the document root.
 */
export function ThemeApplicator() {
  const { data: settings } = useSiteSettings();

  useEffect(() => {
    if (!settings?.theme) return;
    const {
      primaryHue, primarySaturation, primaryLightness,
      accentHue, accentSaturation, accentLightness,
      borderRadius, fontFamily, headingFont, fontScale,
    } = settings.theme;
    const root = document.documentElement;

    // Apply primary color to both light and dark themes
    const lightL = Math.max(primaryLightness - 5, 30);
    const darkL = primaryLightness;

    // We set on :root directly — works for both modes since we override
    root.style.setProperty("--primary", `${primaryHue} ${primarySaturation}% ${darkL}%`);
    root.style.setProperty(
      "--accent",
      `${accentHue ?? primaryHue} ${accentSaturation ?? primarySaturation}% ${accentLightness ?? darkL}%`,
    );
    root.style.setProperty("--ring", `${primaryHue} ${primarySaturation}% ${darkL}%`);
    root.style.setProperty("--sidebar-primary", `${primaryHue} ${primarySaturation}% ${darkL}%`);
    root.style.setProperty("--sidebar-ring", `${primaryHue} ${primarySaturation}% ${darkL}%`);

    // Border radius
    root.style.setProperty("--radius", `${borderRadius}rem`);

    // Global text scale
    root.style.fontSize = `${16 * (fontScale || 1)}px`;

    // Font
    const families = [fontFamily, headingFont].filter(
      (f, i, arr): f is string => !!f && f !== "Inter" && arr.indexOf(f) === i,
    );
    if (families.length > 0) {
      const linkId = "dynamic-google-font";
      let link = document.getElementById(linkId) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.id = linkId;
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
      link.href = `https://fonts.googleapis.com/css2?${families
        .map((f) => `family=${encodeURIComponent(f)}:wght@300;400;500;600;700;800;900`)
        .join("&")}&display=swap`;
    }
    document.body.style.fontFamily = `'${fontFamily || "Inter"}', system-ui, -apple-system, sans-serif`;

    // Update text-gradient CSS
    const gradientHue2 = (primaryHue + 12) % 360;
    const style = document.getElementById("dynamic-theme-style") || document.createElement("style");
    style.id = "dynamic-theme-style";
    style.textContent = `
      .text-gradient {
        background-image: linear-gradient(135deg, hsl(${primaryHue} ${primarySaturation}% ${darkL}%), hsl(${gradientHue2} 100% 65%)) !important;
      }
      .glow-primary {
        box-shadow: 0 0 40px -10px hsl(${primaryHue} ${primarySaturation}% ${darkL}% / 0.4) !important;
      }
      h1, h2, h3, h4, h5, h6 {
        font-family: '${headingFont || fontFamily || "Inter"}', system-ui, -apple-system, sans-serif;
      }
    `;
    if (!style.parentNode) document.head.appendChild(style);

  }, [settings?.theme]);

  return null;
}
